import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Divider } from "primereact/divider";
import { useNavigate } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";

import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { Chip } from "primereact/chip";
import UserAddressService from "../../services/useraddress/useraddress.service";
import { SubscriptionProductModal } from "./SubscriptionProductModal";

type AddressOption = { id: number; label: string };
type Props = {
  visible: boolean;
  onHide: () => void;
  onAddProducts: (partial: { 
    dayOfMonth: number; 
    deliveryAddressId: number; 
    frequencyValue: number;
    products: { productId: number; quantity: number }[];
  }) => void;
};

const NewSubscriptionModal = ({ visible, onHide, onAddProducts }: Props) => {
  const [dayOfMonth, setDayOfMonth] = useState<number | null>(null);
  const [addresses, setAddresses] = useState<AddressOption[]>([]);
  const [addressId, setAddressId] = useState<number | null>(null);
  const [frequencyValue, setFrequencyValue] = useState<number>(30);

  const [selectedProducts, setSelectedProducts] = useState<{productId: number; quantity: number}[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [subscriptionProductVisible, setSubscriptionProductVisible] = useState(false);
  const navigate = useNavigate();

  const getDeliveryAddresses = async (): Promise<AddressOption[]> => {
    try {
      const userAddresses = await UserAddressService.getUserAddress(0);
      if (userAddresses && userAddresses.length > 0) {
        return userAddresses
          .filter((address: any) => !address.isDeleted) 
          .map((address: any) => ({
            id: address.userAddressID,
            label: `${address.userName || 'Address'} • ${address.address1}${address.address2 ? ', ' + address.address2 : ''}, ${address.cityName || ''}`
          }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching user addresses:", error);
      return [];
    }
  };

  useEffect(() => {
    if (visible) {
      getDeliveryAddresses().then(setAddresses);
    }
  }, [visible]);

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const canContinue = dayOfMonth != null && addressId != null && frequencyValue > 0;

  const resetForm = () => {
    setDayOfMonth(null);
    setAddressId(null);
    setFrequencyValue(30);
    setSelectedProducts([]);
    setSelectedDate(null);
    setShowCalendar(false);
    setSubscriptionProductVisible(false);
  };

  const getFrequencyText = (days: number) => {
    if (days === 7) return "Weekly";
    if (days === 14) return "Bi-weekly";
    if (days === 30) return "Monthly";
    if (days === 60) return "Bi-monthly";
    if (days === 90) return "Quarterly";
    return `Every ${days} days`;
  };

  const getSelectedAddressLabel = () => {
    const selected = addresses.find(addr => addr.id === addressId);
    return selected ? selected.label : "No address selected";
  };

  const getNextDeliveryDate = () => {
    if (!dayOfMonth) return null;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const currentDay = today.getDate();

    let nextDelivery = new Date(currentYear, currentMonth, dayOfMonth);

    if (dayOfMonth <= currentDay) {
      nextDelivery = new Date(currentYear, currentMonth + 1, dayOfMonth);
    }

    return nextDelivery;
  };

  const getDaysUntilDelivery = () => {
    const nextDate = getNextDeliveryDate();
    if (!nextDate) return 0;

    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDeliveryDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleProductConfirmation = (confirmedProducts: any[]) => {
    const subscriptionProducts = confirmedProducts.map((item: any) => ({
      productId: item.productId,
      quantity: item.quantity
    }));
    setSelectedProducts(subscriptionProducts);
    setSubscriptionProductVisible(false);
  };

  const getCurrentProductsForModal = () => {
    return selectedProducts.map(product => ({
      productId: product.productId,
      productName: `Product ${product.productId}`, 
      quantity: product.quantity,
      price: 0, 
      imageUrl: '' 
    }));
  };

  const handleCreateSubscription = () => {
    if (!dayOfMonth || !addressId) return;
    
    onAddProducts({
      dayOfMonth,
      deliveryAddressId: addressId,
      frequencyValue,
      products: selectedProducts
    });
    onHide();
  };

  const footer = (
    <div className="flex justify-content-between align-items-center w-full p-3 surface-50 border-round">
      <div className="flex align-items-center gap-2">
        {selectedProducts.length > 0 && (
          <Chip
            label={`${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''} selected`}
            icon="pi pi-shopping-cart"
            className="p-chip-success"
          />
        )}
        {dayOfMonth && (
          <Chip
            label={`Day ${dayOfMonth} of month`}
            icon="pi pi-calendar"
            className="p-chip-info"
          />
        )}
        {frequencyValue && (
          <Chip
            label={getFrequencyText(frequencyValue)}
            icon="pi pi-clock"
            className="p-chip-warning"
          />
        )}
      </div>
      <div className="flex gap-2">
        <Button
          label="Reset"
          icon="pi pi-refresh"
          className="p-button-secondary p-button-outlined"
          onClick={resetForm}
        />
        <Button
          label={selectedProducts.length ? "Create Subscription" : "Add Products"}
          icon={selectedProducts.length ? "pi pi-check" : "pi pi-shopping-cart"}
          disabled={!canContinue}
          onClick={selectedProducts.length ? handleCreateSubscription : () => setSubscriptionProductVisible(true)}
          className={selectedProducts.length ? "p-button-success" : ""}
        />
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-calendar-plus text-primary"></i>
            <span>Create New Subscription</span>
          </div>
        }
        visible={visible}
        style={{ width: "50rem", maxWidth: "95vw" }}
        onHide={onHide}
        footer={footer}
        modal
        className="subscription-dialog"
      >
        <div className="grid">
          {/* Day of Month Selection */}
          <div className="col-12 lg:col-6">
            <Card className="h-full">
              <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-calendar text-primary"></i>
                <div className="text-900 font-semibold">Delivery Day</div>
              </div>
              <div className="text-600 mb-3">
                Choose your preferred delivery day of each month (1–31)
              </div>

              <div className="flex align-items-center gap-2 mb-3">
                <Button
                  label={showCalendar ? "Hide Calendar" : "Show Calendar"}
                  icon={showCalendar ? "pi pi-eye-slash" : "pi pi-calendar"}
                  className="p-button-outlined p-button-sm"
                  onClick={() => setShowCalendar(!showCalendar)}
                />
                {dayOfMonth && (
                  <Badge
                    value={`Day ${dayOfMonth}`}
                    severity="success"
                  />
                )}
              </div>

              {showCalendar && (
                <div className="mb-3">
                  <Calendar
                    value={selectedDate}
                    onChange={(e) => {
                      const date = e.value as Date;
                      setSelectedDate(date);
                      if (date && date instanceof Date) {
                        setDayOfMonth(date.getDate());
                      }
                    }}
                    inline
                    showButtonBar={false}
                    className="w-full"
                  />
                </div>
              )}

              <div className="surface-50 border-1 border-200 p-3 border-round">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "0.5rem"
                  }}
                >
                  {days.map((d) => {
                    const selected = dayOfMonth === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDayOfMonth(d)}
                        className={`p-2 border-round text-center cursor-pointer transition-all transition-duration-200 ${
                          selected
                            ? "bg-primary text-0 shadow-2"
                            : "surface-100 text-800 hover:surface-200"
                        }`}
                        style={{
                          border: "none",
                          minHeight: "2.5rem",
                          fontWeight: selected ? "bold" : "normal"
                        }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Card>
          </div>

          {/* Address and Frequency */}
          <div className="col-12 lg:col-6">
            <Card className="h-full">
              {/* Address Section */}
              <div className="mb-4">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-map-marker text-primary"></i>
                  <div className="text-900 font-semibold">Delivery Address</div>
                </div>
                <Dropdown
                  value={addressId}
                  onChange={(e) => setAddressId(e.value)}
                  options={addresses}
                  optionLabel="label"
                  optionValue="id"
                  placeholder="Choose delivery address"
                  className="w-full"
                  showClear
                />
                {addressId && (
                  <div className="mt-2 p-2 surface-100 border-round text-sm">
                    <i className="pi pi-info-circle mr-2"></i>
                    {getSelectedAddressLabel()}
                  </div>
                )}
              </div>
              <Button
              label="Add new"
              icon="pi pi-plus"
              className="p-button-text"
              onClick={() => {
                onHide();
                navigate(RouteConstant.userAddresses);
              }}
            />

              <Divider />

              {/* Frequency Section */}
              <div>
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-clock text-primary"></i>
                  <div className="text-900 font-semibold">Delivery Frequency</div>
                </div>
                <div className="flex align-items-center gap-3 mb-3">
                  <InputNumber
                    value={frequencyValue}
                    onValueChange={(e) => setFrequencyValue(e.value || 0)}
                    showButtons
                    min={1}
                    max={90}
                    suffix=" days"
                    className="w-full"
                  />
                  <Badge
                    value={getFrequencyText(frequencyValue)}
                    severity="info"
                    className="text-sm"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[7, 14, 30, 60, 90].map(days => (
                    <Button
                      key={days}
                      label={getFrequencyText(days)}
                      className={`p-button-sm ${frequencyValue === days ? 'p-button-success' : 'p-button-outlined'}`}
                      onClick={() => setFrequencyValue(days)}
                    />
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* Delivery Preview */}
          {dayOfMonth && frequencyValue && (
            <div className="col-12">
              <Card className="bg-primary-50 border-primary">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-calendar-check text-primary"></i>
                  <div className="text-900 font-semibold">Next Delivery Preview</div>
                </div>
                {(() => {
                  const nextDate = getNextDeliveryDate();
                  const daysLeft = getDaysUntilDelivery();

                  if (nextDate) {
                    return (
                      <div className="flex align-items-center gap-3">
                        <div className="flex-1">
                          <div className="text-900 font-medium">
                            Your subscribed products will be delivered on{' '}
                            <span className="font-bold text-primary">
                              {formatDeliveryDate(nextDate)}
                            </span>
                          </div>
                          <div className="text-600 mt-1">
                            Then every {frequencyValue} days ({getFrequencyText(frequencyValue).toLowerCase()})
                          </div>
                        </div>
                        <div className="text-center">
                          <Badge
                            value={`${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                            severity={daysLeft <= 7 ? 'success' : daysLeft <= 14 ? 'warning' : 'info'}
                            className="text-lg p-2"
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </Card>
            </div>
          )}
        </div>

        <Divider />

        {/* Selected Products Preview */}
        {selectedProducts.length > 0 && (
          <div className="col-12 mt-3">
            <Card>
              <div className="flex align-items-center justify-content-between mb-3">
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-shopping-cart text-primary"></i>
                  <div className="text-900 font-semibold">Selected Products</div>
                  <Badge value={selectedProducts.length} severity="success" />
                </div>
                <Button
                  label="Edit Products"
                  icon="pi pi-pencil"
                  className="p-button-outlined p-button-sm"
                  onClick={() => setSubscriptionProductVisible(true)}
                />
              </div>
              <div className="grid">
                {selectedProducts.map((product, index) => (
                  <div key={index} className="col-12 md:col-6 lg:col-4">
                    <div className="surface-100 p-3 border-round flex align-items-center justify-content-between">
                      <div>
                        <div className="font-semibold">Product #{product.productId}</div>
                        <div className="text-600 text-sm">Quantity: {product.quantity}</div>
                      </div>
                      <Badge value={`×${product.quantity}`} severity="info" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </Dialog>

      {/* Subscription Product Management Modal */}
      <SubscriptionProductModal
        visible={subscriptionProductVisible}
        onHide={() => setSubscriptionProductVisible(false)}
        onConfirm={handleProductConfirmation}
        initialProducts={getCurrentProductsForModal()}
      />
    </>
  );
};

export default NewSubscriptionModal;